import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Lock, Loader2, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // 2FA challenge state
  const [twofaChallenge, setTwofaChallenge] = useState(null); // { challenge_token, message }
  const [twofaCode, setTwofaCode] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const finalizeLogin = (data) => {
    toast.success('Connexion réussie');
    localStorage.setItem('token', data.token);
    // Stocke isAdmin synchrone — utilisé par MaintenanceGate sans appel API
    if (data.user) {
      localStorage.setItem('isAdmin', data.user.isAdmin === true ? '1' : '0');
      localStorage.setItem('userEmail', data.user.email || '');
    }
    navigate('/dashboard', { state: { user: data.user } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData, {
        withCredentials: true
      });

      if (response.data.status === '2fa_required') {
        setTwofaChallenge({
          challenge_token: response.data.challenge_token,
          message: response.data.message,
        });
        toast.info('Entre ton code 2FA pour finaliser la connexion');
      } else if (response.data.status === 'success') {
        finalizeLogin(response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit2FA = async (e) => {
    e.preventDefault();
    if (twofaCode.length !== 6) {
      toast.error('Code à 6 chiffres requis');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-2fa`, {
        challenge_token: twofaChallenge.challenge_token,
        code: twofaCode,
      }, { withCredentials: true });

      if (response.data.status === 'success') {
        if (response.data.backup_used) {
          toast.warning('Code de secours utilisé — pense à régénérer tes codes');
        }
        finalizeLogin(response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Code invalide';
      toast.error(message);
      setTwofaCode('');
    } finally {
      setLoading(false);
    }
  };

  const cancelTwofa = () => {
    setTwofaChallenge(null);
    setTwofaCode('');
  };
  
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-4">
      <div className="absolute inset-0 grid-bg opacity-20" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="font-orbitron text-2xl font-bold text-cyan-400 glitch-subtle" data-testid="logo">
            L'EFFACEUR
          </Link>
          <p className="text-gray-500 font-mono text-sm mt-2">// AUTHENTIFICATION_REQUISE</p>
        </div>
        
        {/* Login Card */}
        <div className="glass-card p-8 border border-gray-800" data-testid="login-form">
          <h1 className="font-orbitron text-xl font-bold text-white mb-6 text-center">
            CONNEXION
          </h1>
          
          {/* Google Login */}
          <Button 
            onClick={handleGoogleLogin}
            className="w-full bg-transparent border border-gray-700 text-gray-300 hover:border-cyan-400 hover:text-cyan-400 mb-6 py-6"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>
          
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4 bg-void text-gray-500 font-mono">OU</span>
            </div>
          </div>
          
          {/* 2FA Challenge Form (apparait quand login retourne 2fa_required) */}
          {twofaChallenge ? (
            <form onSubmit={handleSubmit2FA} className="space-y-5">
              <div className="text-center mb-4">
                <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-2" />
                <h2 className="font-orbitron text-lg text-white">Code 2FA requis</h2>
                <p className="text-xs text-gray-400 mt-1">{twofaChallenge.message || 'Entre le code à 6 chiffres de ton app authenticator'}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 font-mono text-xs uppercase tracking-wider">Code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="bg-gray-900/50 border-gray-800 text-white text-center text-2xl font-mono tracking-widest focus:border-cyan-400"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || twofaCode.length !== 6}
                className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-bold py-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Valider le code <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
              <button type="button" onClick={cancelTwofa} className="w-full text-xs text-gray-500 hover:text-white mt-2">
                Annuler et revenir au login
              </button>
              <p className="text-[10px] text-gray-600 text-center mt-4">
                Tu peux aussi entrer un code de secours à 8 caractères si tu n'as pas accès à ton authenticator.
              </p>
            </form>
          ) : (
          <>
          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.fr"
                  className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                Mot de passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron uppercase tracking-wider py-6"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Se Connecter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            
            <div className="text-right">
              <Link to="/forgot-password" className="text-gray-500 hover:text-cyan-400 text-sm transition-colors" data-testid="forgot-password-link">
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <p className="text-center text-gray-500 text-sm mt-6">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-cyan-400 hover:underline" data-testid="register-link">
              Créer un compte
            </Link>
          </p>
          </>
          )}
        </div>
        
        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-cyan-400 text-sm font-mono transition-colors">
            ← Retour à l'accueil
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
